import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import QRCode from 'qrcode'

const UpiQrGenerator = () => {
  const { userClaims } = useAuth()
  const [upiUri, setUpiUri] = useState('')
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [qrSize, setQrSize] = useState('medium')
  const [isGenerating, setIsGenerating] = useState(false)
  const [validationError, setValidationError] = useState('')
  const canvasRef = useRef(null)

  // Redirect if not super admin
  if (!userClaims?.sadmin) {
    return <Navigate to="/devices" replace />
  }

  const sizeOptions = {
    small: { width: 200, label: 'Small (200x200)' },
    medium: { width: 400, label: 'Medium (400x400)' },
    large: { width: 800, label: 'Large (800x800)' }
  }

  const validateUpiUri = (uri) => {
    if (!uri.trim()) {
      return 'UPI URI cannot be empty'
    }

    // Basic UPI URI format validation
    const upiPattern = /^upi:\/\/pay\?/i
    if (!upiPattern.test(uri)) {
      return 'UPI URI must start with "upi://pay?"'
    }

    // Check for required parameter 'pa' (payee address)
    const paPattern = /[&?]pa=([^&]+)/i
    if (!paPattern.test(uri)) {
      return 'UPI URI must include payee address (pa parameter)'
    }

    return ''
  }

  const generateQrCode = async (uri, size = 'medium') => {
    if (!uri.trim()) {
      setQrCodeUrl('')
      return
    }

    const error = validateUpiUri(uri)
    if (error) {
      setValidationError(error)
      setQrCodeUrl('')
      return
    }

    setValidationError('')
    setIsGenerating(true)

    try {
      const qrCodeDataUrl = await QRCode.toDataURL(uri, {
        width: sizeOptions[size].width,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      })
      
      setQrCodeUrl(qrCodeDataUrl)
    } catch (error) {
      console.error('Error generating QR code:', error)
      setValidationError('Failed to generate QR code. Please check your UPI URI.')
    } finally {
      setIsGenerating(false)
    }
  }

  const downloadQrCode = () => {
    if (!qrCodeUrl) return

    const link = document.createElement('a')
    link.download = `upi-qr-${Date.now()}.png`
    link.href = qrCodeUrl
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const clearAll = () => {
    setUpiUri('')
    setQrCodeUrl('')
    setValidationError('')
  }

  // Generate QR code when URI or size changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      generateQrCode(upiUri, qrSize)
    }, 300) // Debounce for 300ms

    return () => clearTimeout(timeoutId)
  }, [upiUri, qrSize])

  // Sample UPI URI for reference
  const sampleUri = 'upi://pay?pa=merchant@paytm&pn=Merchant%20Name&am=100.00&cu=INR&tn=Payment%20for%20goods'

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">UPI QR Code Generator</h2>
              <p className="text-muted mb-0">
                <i className="fas fa-shield-alt text-warning me-2"></i>
                Super Admin Only - Generate downloadable UPI payment QR codes
              </p>
            </div>
            <div className="d-flex gap-2">
              <button 
                className="btn btn-outline-secondary"
                onClick={clearAll}
                disabled={!upiUri && !qrCodeUrl}
              >
                <i className="fas fa-eraser me-2"></i>
                Clear All
              </button>
            </div>
          </div>

          <div className="row">
            {/* Input Section */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white">
                  <h5 className="mb-0">
                    <i className="fas fa-keyboard me-2"></i>
                    UPI URI Input
                  </h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">UPI Payment URI</label>
                    <textarea
                      className={`form-control ${validationError ? 'is-invalid' : qrCodeUrl ? 'is-valid' : ''}`}
                      rows="4"
                      value={upiUri}
                      onChange={(e) => setUpiUri(e.target.value)}
                      placeholder="Enter UPI URI (e.g., upi://pay?pa=merchant@bank&pn=Name&am=100&cu=INR)"
                      style={{ fontFamily: 'monospace', fontSize: '14px' }}
                    />
                    {validationError && (
                      <div className="invalid-feedback">
                        {validationError}
                      </div>
                    )}
                    {qrCodeUrl && !validationError && (
                      <div className="valid-feedback">
                        Valid UPI URI - QR code generated successfully
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">QR Code Size</label>
                    <select
                      className="form-select"
                      value={qrSize}
                      onChange={(e) => setQrSize(e.target.value)}
                    >
                      {Object.entries(sizeOptions).map(([key, option]) => (
                        <option key={key} value={key}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="alert alert-info">
                    <h6 className="alert-heading">
                      <i className="fas fa-info-circle me-2"></i>
                      UPI URI Format
                    </h6>
                    <p className="mb-2">Required parameters:</p>
                    <ul className="mb-2">
                      <li><code>pa</code> - Payee address (UPI ID)</li>
                    </ul>
                    <p className="mb-2">Optional parameters:</p>
                    <ul className="mb-0">
                      <li><code>pn</code> - Payee name</li>
                      <li><code>am</code> - Amount</li>
                      <li><code>cu</code> - Currency (INR)</li>
                      <li><code>tn</code> - Transaction note</li>
                    </ul>
                  </div>

                  <div className="mb-0">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setUpiUri(sampleUri)}
                      disabled={isGenerating}
                    >
                      <i className="fas fa-magic me-2"></i>
                      Load Sample URI
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code Preview Section */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-qrcode me-2"></i>
                    QR Code Preview
                  </h5>
                  {qrCodeUrl && (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={downloadQrCode}
                    >
                      <i className="fas fa-download me-2"></i>
                      Download PNG
                    </button>
                  )}
                </div>
                <div className="card-body text-center">
                  {isGenerating ? (
                    <div className="py-5">
                      <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Generating...</span>
                      </div>
                      <div>Generating QR Code...</div>
                    </div>
                  ) : qrCodeUrl ? (
                    <div className="py-3">
                      <img
                        src={qrCodeUrl}
                        alt="UPI QR Code"
                        className="img-fluid border rounded shadow-sm"
                        style={{ maxWidth: '100%', height: 'auto' }}
                      />
                      <div className="mt-3">
                        <small className="text-muted d-block">
                          Size: {sizeOptions[qrSize].label}
                        </small>
                        <small className="text-success">
                          <i className="fas fa-check-circle me-1"></i>
                          Ready for download
                        </small>
                      </div>
                    </div>
                  ) : (
                    <div className="py-5 text-muted">
                      <i className="fas fa-qrcode fa-3x mb-3 d-block opacity-50"></i>
                      <p>Enter a valid UPI URI to generate QR code</p>
                      <small>The QR code will appear here automatically</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpiQrGenerator